import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import type { User } from "firebase/auth";
import {
  collection,
  collectionGroup,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  getDoc,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type FieldValue,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import type {
  Asset,
  AssetReservation,
  Company,
  ExternalService,
  ExternalServiceEvent,
  ExternalServiceEventType,
  ExternalServiceItem,
  ExternalServiceItemStatus,
  ExternalServiceParticipant,
  ExternalServiceStatus
} from "../types/imobi";
import { findConflictingReservation } from "../domain/reservations";
import { db } from "./firebase";

export type CreateExternalServiceInput = {
  companyId: string;
  title: string;
  description: string;
  startDate: string;
  expectedEndDate?: string;
  location?: string;
  clientName?: string;
  jobType?: string;
  notes?: string;
  participantUserIds: string[];
  allowSelfEnrollment?: boolean;
  allowSelfAssignAssets?: boolean;
  allowPeerItemTransfer?: boolean;
};

export type ExternalServiceParticipantInput = {
  userId: string;
  userName: string;
  userEmail?: string;
  roleInService?: string;
};

type PdfImage = {
  dataUrl: string;
  format: "PNG" | "JPEG";
};

const IMAGE_LOAD_TIMEOUT_MS = 6000;

function dataUrlFormat(dataUrl: string): "PNG" | "JPEG" | null {
  if (dataUrl.startsWith("data:image/png")) {
    return "PNG";
  }

  if (dataUrl.startsWith("data:image/jpeg") || dataUrl.startsWith("data:image/jpg")) {
    return "JPEG";
  }

  return null;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs = IMAGE_LOAD_TIMEOUT_MS): Promise<T | null> {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), timeoutMs);
    promise
      .then((result) => resolve(result))
      .catch(() => resolve(null))
      .finally(() => window.clearTimeout(timeout));
  });
}

function readBlobAsDataUrl(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

async function normalizeImageDataUrl(dataUrl: string, preserveTransparency: boolean): Promise<PdfImage | null> {
  const directFormat = dataUrlFormat(dataUrl);
  if (preserveTransparency && directFormat) {
    return { dataUrl, format: directFormat };
  }

  return new Promise((resolve) => {
    const image = new window.Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const context = canvas.getContext("2d");
        if (!context || !canvas.width || !canvas.height) {
          resolve(null);
          return;
        }

        if (!preserveTransparency) {
          context.fillStyle = "#ffffff";
          context.fillRect(0, 0, canvas.width, canvas.height);
        }

        context.drawImage(image, 0, 0);
        const output = preserveTransparency ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", 0.82);
        resolve({ dataUrl: output, format: preserveTransparency ? "PNG" : "JPEG" });
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
}

async function loadImageFromUrlCanvas(url: string, preserveTransparency: boolean): Promise<PdfImage | null> {
  return new Promise((resolve) => {
    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const context = canvas.getContext("2d");
        if (!context || !canvas.width || !canvas.height) {
          resolve(null);
          return;
        }
        if (!preserveTransparency) {
          context.fillStyle = "#ffffff";
          context.fillRect(0, 0, canvas.width, canvas.height);
        }

        context.drawImage(image, 0, 0);
        const output = preserveTransparency ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", 0.82);
        resolve({ dataUrl: output, format: preserveTransparency ? "PNG" : "JPEG" });
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

async function loadPdfImage(url: string | undefined, preserveTransparency = false): Promise<PdfImage | null> {
  if (!url) {
    return null;
  }

  try {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), IMAGE_LOAD_TIMEOUT_MS);
    const response = await fetch(url, { mode: "cors", cache: "force-cache", signal: controller.signal });
    window.clearTimeout(timeout);
    if (!response.ok) {
      return withTimeout(loadImageFromUrlCanvas(url, preserveTransparency));
    }

    const dataUrl = await readBlobAsDataUrl(await response.blob());
    if (dataUrl) {
      const normalized = await normalizeImageDataUrl(dataUrl, preserveTransparency);
      if (normalized) {
        return normalized;
      }
    }

    return withTimeout(loadImageFromUrlCanvas(url, preserveTransparency));
  } catch {
    return withTimeout(loadImageFromUrlCanvas(url, preserveTransparency));
  }
}

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function userName(user: User | null) {
  return user?.displayName ?? user?.email ?? user?.uid ?? "Usuario";
}

function externalServiceCode(serviceId: string) {
  return `SE-${serviceId.slice(0, 8).toUpperCase()}`;
}

function externalServiceQrPayload(companyId: string, serviceId: string, serviceCode?: string) {
  return `imobi://external-service/${companyId}/${serviceId}/${serviceCode ?? externalServiceCode(serviceId)}`;
}

function mapExternalService(companyId: string, id: string, data: Record<string, unknown>): ExternalService {
  return {
    id,
    companyId,
    serviceCode: data.serviceCode ? String(data.serviceCode) : externalServiceCode(id),
    title: String(data.title ?? ""),
    description: String(data.description ?? ""),
    startDate: serializeTimestamp(data.startDate) || String(data.startDate ?? ""),
    expectedEndDate: serializeTimestamp(data.expectedEndDate) || (data.expectedEndDate ? String(data.expectedEndDate) : undefined),
    actualEndDate: serializeTimestamp(data.actualEndDate) || (data.actualEndDate ? String(data.actualEndDate) : undefined),
    location: data.location ? String(data.location) : undefined,
    clientName: data.clientName ? String(data.clientName) : undefined,
    jobType: data.jobType ? String(data.jobType) : undefined,
    status: (data.status ?? "aberto") as ExternalServiceStatus,
    allowSelfEnrollment: data.allowSelfEnrollment === true,
    allowSelfAssignAssets: data.allowSelfAssignAssets === true,
    allowPeerItemTransfer: data.allowPeerItemTransfer === true,
    createdBy: String(data.createdBy ?? ""),
    createdByName: String(data.createdByName ?? ""),
    createdAt: serializeTimestamp(data.createdAt),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined,
    updatedByName: data.updatedByName ? String(data.updatedByName) : undefined,
    notes: data.notes ? String(data.notes) : undefined,
    participantsCount: typeof data.participantsCount === "number" ? data.participantsCount : 0,
    assetsCount: typeof data.assetsCount === "number" ? data.assetsCount : 0,
    confirmedAssetsCount: typeof data.confirmedAssetsCount === "number" ? data.confirmedAssetsCount : 0,
    returnedAssetsCount: typeof data.returnedAssetsCount === "number" ? data.returnedAssetsCount : 0,
    divergentAssetsCount: typeof data.divergentAssetsCount === "number" ? data.divergentAssetsCount : 0,
    deletedAt: serializeTimestamp(data.deletedAt) || undefined,
    deletedBy: data.deletedBy ? String(data.deletedBy) : undefined
  };
}

function mapParticipant(id: string, data: Record<string, unknown>): ExternalServiceParticipant {
  return {
    id,
    userId: String(data.userId ?? ""),
    userName: String(data.userName ?? ""),
    userEmail: data.userEmail ? String(data.userEmail) : undefined,
    roleInService: data.roleInService ? String(data.roleInService) : undefined,
    status: (data.status ?? "pendente") as ExternalServiceParticipant["status"],
    addedBy: String(data.addedBy ?? ""),
    addedAt: serializeTimestamp(data.addedAt)
  };
}

function mapItem(id: string, data: Record<string, unknown>): ExternalServiceItem {
  return {
    id,
    assetId: String(data.assetId ?? ""),
    assetCode: data.assetCode ? String(data.assetCode) : undefined,
    patrimonyNumber: String(data.patrimonyNumber ?? ""),
    internalCode: data.internalCode ? String(data.internalCode) : undefined,
    serialNumber: data.serialNumber ? String(data.serialNumber) : undefined,
    description: String(data.description ?? ""),
    mainPhotoUrl: data.mainPhotoUrl ? String(data.mainPhotoUrl) : undefined,
    assignedToUserId: data.assignedToUserId ? String(data.assignedToUserId) : undefined,
    assignedToUserName: data.assignedToUserName ? String(data.assignedToUserName) : undefined,
    assignmentMode: (data.assignmentMode ?? "gerente_atribuiu") as ExternalServiceItem["assignmentMode"],
    status: (data.status ?? "pendente_confirmacao") as ExternalServiceItemStatus,
    plannedStartAt: serializeTimestamp(data.plannedStartAt) || (data.plannedStartAt ? String(data.plannedStartAt) : undefined),
    expectedReturnAt: serializeTimestamp(data.expectedReturnAt) || (data.expectedReturnAt ? String(data.expectedReturnAt) : undefined),
    actualReturnAt: serializeTimestamp(data.actualReturnAt) || undefined,
    confirmedByUserId: data.confirmedByUserId ? String(data.confirmedByUserId) : undefined,
    confirmedByUserName: data.confirmedByUserName ? String(data.confirmedByUserName) : undefined,
    confirmedAt: serializeTimestamp(data.confirmedAt),
    approvedByUserId: data.approvedByUserId ? String(data.approvedByUserId) : undefined,
    approvedByUserName: data.approvedByUserName ? String(data.approvedByUserName) : undefined,
    approvedAt: serializeTimestamp(data.approvedAt),
    exitCheckedByUserId: data.exitCheckedByUserId ? String(data.exitCheckedByUserId) : undefined,
    exitCheckedByUserName: data.exitCheckedByUserName ? String(data.exitCheckedByUserName) : undefined,
    exitCheckedAt: serializeTimestamp(data.exitCheckedAt),
    returnedByUserId: data.returnedByUserId ? String(data.returnedByUserId) : undefined,
    returnedByUserName: data.returnedByUserName ? String(data.returnedByUserName) : undefined,
    returnedAt: serializeTimestamp(data.returnedAt),
    returnCheckedByUserId: data.returnCheckedByUserId ? String(data.returnCheckedByUserId) : undefined,
    returnCheckedByUserName: data.returnCheckedByUserName ? String(data.returnCheckedByUserName) : undefined,
    returnCheckedAt: serializeTimestamp(data.returnCheckedAt),
    conditionBefore: data.conditionBefore ? String(data.conditionBefore) : undefined,
    conditionAfter: data.conditionAfter ? String(data.conditionAfter) : undefined,
    exitNotes: data.exitNotes ? String(data.exitNotes) : undefined,
    approvalNotes: data.approvalNotes ? String(data.approvalNotes) : undefined,
    returnNotes: data.returnNotes ? String(data.returnNotes) : undefined,
    divergenceNotes: data.divergenceNotes ? String(data.divergenceNotes) : undefined,
    resolutionNotes: data.resolutionNotes ? String(data.resolutionNotes) : undefined,
    resolvedByUserId: data.resolvedByUserId ? String(data.resolvedByUserId) : undefined,
    resolvedByUserName: data.resolvedByUserName ? String(data.resolvedByUserName) : undefined,
    resolvedAt: serializeTimestamp(data.resolvedAt),
    pickupInspectionId: data.pickupInspectionId ? String(data.pickupInspectionId) : undefined,
    returnInspectionId: data.returnInspectionId ? String(data.returnInspectionId) : undefined,
    createdAt: serializeTimestamp(data.createdAt),
    updatedAt: serializeTimestamp(data.updatedAt)
  };
}

function mapEvent(id: string, data: Record<string, unknown>): ExternalServiceEvent {
  return {
    id,
    type: (data.type ?? "servico_criado") as ExternalServiceEventType,
    userId: String(data.userId ?? ""),
    userName: String(data.userName ?? ""),
    assetId: data.assetId ? String(data.assetId) : undefined,
    participantUserId: data.participantUserId ? String(data.participantUserId) : undefined,
    description: String(data.description ?? ""),
    createdAt: serializeTimestamp(data.createdAt)
  };
}

function mapReservation(id: string, data: Record<string, unknown>): AssetReservation {
  return {
    id,
    companyId: String(data.companyId ?? ""),
    assetId: String(data.assetId ?? ""),
    serviceId: String(data.serviceId ?? ""),
    serviceTitle: String(data.serviceTitle ?? ""),
    serviceCode: data.serviceCode ? String(data.serviceCode) : undefined,
    itemId: String(data.itemId ?? ""),
    responsibleUserId: data.responsibleUserId ? String(data.responsibleUserId) : undefined,
    responsibleUserName: data.responsibleUserName ? String(data.responsibleUserName) : undefined,
    plannedStartAt: serializeTimestamp(data.plannedStartAt) || String(data.plannedStartAt ?? ""),
    expectedReturnAt: serializeTimestamp(data.expectedReturnAt) || (data.expectedReturnAt ? String(data.expectedReturnAt) : undefined),
    status: (data.status ?? "reservado") as AssetReservation["status"],
    actualStartAt: serializeTimestamp(data.actualStartAt) || undefined,
    actualReturnAt: serializeTimestamp(data.actualReturnAt) || undefined,
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

export function subscribeAssetReservations(companyId: string | undefined, assetId: string | undefined, onChange: (reservations: AssetReservation[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId || !assetId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "assets", assetId, "reservations"), orderBy("plannedStartAt")),
    (snapshot) => onChange(snapshot.docs.map((reservationDoc) => mapReservation(reservationDoc.id, reservationDoc.data()))),
    (error) => onError(error.message)
  );
}

/**
 * Todas as reservas de bens da empresa (consulta de grupo em assets/{id}/reservations).
 * Alimenta o calendario, a ocupacao por equipamento e as metricas de uso.
 */
export function subscribeCompanyReservations(companyId: string | undefined, onChange: (reservations: AssetReservation[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collectionGroup(db, "reservations"), where("companyId", "==", companyId), orderBy("plannedStartAt")),
    (snapshot) => onChange(snapshot.docs.map((reservationDoc) => mapReservation(reservationDoc.id, reservationDoc.data()))),
    (error) => onError(error.message)
  );
}

async function loadActiveAssetReservations(companyId: string, assetId: string): Promise<AssetReservation[]> {
  if (!db) {
    return [];
  }

  const snapshot = await getDocs(
    query(
      collection(db, "companies", companyId, "assets", assetId, "reservations"),
      where("status", "in", ["reservado", "em_uso"])
    )
  );
  return snapshot.docs.map((reservationDoc) => mapReservation(reservationDoc.id, reservationDoc.data()));
}

export async function checkAssetReservationConflict(companyId: string, assetId: string, plannedStartAt: string, expectedReturnAt: string | undefined, excludeServiceId?: string): Promise<AssetReservation | undefined> {
  const activeReservations = await loadActiveAssetReservations(companyId, assetId);
  return findConflictingReservation(activeReservations, plannedStartAt, expectedReturnAt, excludeServiceId);
}

export function buildReservationConflictMessage(conflict: AssetReservation): string {
  const period = conflict.expectedReturnAt
    ? `de ${formatPdfDate(conflict.plannedStartAt)} até ${formatPdfDate(conflict.expectedReturnAt)}`
    : `a partir de ${formatPdfDate(conflict.plannedStartAt)}`;
  const responsible = conflict.responsibleUserName ? ` Responsável: ${conflict.responsibleUserName}.` : "";
  return `Este equipamento já está reservado neste período. Serviço: ${conflict.serviceTitle} (${period}).${responsible}`;
}

async function syncAssetExternalServiceState(companyId: string, assetId: string, user: User): Promise<void> {
  if (!db) {
    return;
  }

  const activeReservations = await loadActiveAssetReservations(companyId, assetId);
  const now = serverTimestamp();
  const assetRef = doc(db, "companies", companyId, "assets", assetId);

  if (!activeReservations.length) {
    const assetSnapshot = await getDoc(assetRef);
    const currentStatus = assetSnapshot.data()?.externalServiceStatus;
    if (currentStatus === "extraviado" || currentStatus === "divergente") {
      return;
    }

    await updateDoc(assetRef, {
      externalServiceStatus: "disponivel",
      currentExternalServiceId: null,
      currentExternalServiceParticipantId: null,
      currentExternalServiceParticipantName: null,
      currentExternalServicePlannedStartAt: null,
      currentExternalServiceExpectedReturnAt: null,
      externalServiceUpdatedAt: now,
      updatedAt: now,
      updatedBy: user.uid
    });
    return;
  }

  const inUse = activeReservations.find((reservation) => reservation.status === "em_uso");
  const next = inUse ?? [...activeReservations].sort((left, right) => left.plannedStartAt.localeCompare(right.plannedStartAt))[0];
  await updateDoc(assetRef, {
    externalServiceStatus: inUse ? "em_servico" : "reservado",
    currentExternalServiceId: next.serviceId,
    currentExternalServiceParticipantName: next.responsibleUserName ?? null,
    currentExternalServicePlannedStartAt: next.plannedStartAt,
    currentExternalServiceExpectedReturnAt: next.expectedReturnAt ?? null,
    externalServiceUpdatedAt: now,
    updatedAt: now,
    updatedBy: user.uid
  });
}

function writeEvent(
  batch: ReturnType<typeof writeBatch>,
  companyId: string,
  serviceId: string,
  type: ExternalServiceEventType,
  user: User,
  description: string,
  extra: { assetId?: string; participantUserId?: string } = {}
) {
  const eventRef = doc(collection(db!, "companies", companyId, "externalServices", serviceId, "events"));
  batch.set(eventRef, {
    type,
    userId: user.uid,
    userName: userName(user),
    description,
    assetId: extra.assetId ?? null,
    participantUserId: extra.participantUserId ?? null,
    createdAt: serverTimestamp()
  });
}

export function subscribeExternalServices(companyId: string | undefined, onChange: (services: ExternalService[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "externalServices"), orderBy("startDate", "desc")),
    (snapshot) => onChange(snapshot.docs.map((serviceDoc) => mapExternalService(companyId, serviceDoc.id, serviceDoc.data()))),
    (error) => onError(error.message)
  );
}

export function subscribeExternalServiceParticipants(companyId: string | undefined, serviceId: string | undefined, onChange: (participants: ExternalServiceParticipant[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId || !serviceId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "externalServices", serviceId, "participants"), orderBy("userName")),
    (snapshot) => onChange(snapshot.docs.map((participantDoc) => mapParticipant(participantDoc.id, participantDoc.data()))),
    (error) => onError(error.message)
  );
}

export function subscribeExternalServiceItems(companyId: string | undefined, serviceId: string | undefined, onChange: (items: ExternalServiceItem[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId || !serviceId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "externalServices", serviceId, "items"), orderBy("patrimonyNumber")),
    (snapshot) => onChange(snapshot.docs.map((itemDoc) => mapItem(itemDoc.id, itemDoc.data()))),
    (error) => onError(error.message)
  );
}

export function subscribeExternalServiceEvents(companyId: string | undefined, serviceId: string | undefined, onChange: (events: ExternalServiceEvent[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId || !serviceId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "externalServices", serviceId, "events"), orderBy("createdAt", "desc")),
    (snapshot) => onChange(snapshot.docs.map((eventDoc) => mapEvent(eventDoc.id, eventDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function createExternalService(input: CreateExternalServiceInput, participants: ExternalServiceParticipantInput[], user: User): Promise<string> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  if (!input.title.trim()) {
    throw new Error("Informe o nome do servico externo.");
  }

  const serviceRef = doc(collection(db, "companies", input.companyId, "externalServices"));
  const batch = writeBatch(db);
  const participantInputs = participants.filter((participant) => input.participantUserIds.includes(participant.userId));
  const now = serverTimestamp();
  const serviceCode = externalServiceCode(serviceRef.id);

  batch.set(serviceRef, {
    companyId: input.companyId,
    serviceCode,
    title: input.title.trim(),
    description: input.description.trim(),
    startDate: input.startDate,
    expectedEndDate: input.expectedEndDate || null,
    actualEndDate: null,
    location: input.location?.trim() || null,
    clientName: input.clientName?.trim() || null,
    jobType: input.jobType?.trim() || null,
    status: "aberto",
    allowSelfEnrollment: input.allowSelfEnrollment === true,
    allowSelfAssignAssets: input.allowSelfAssignAssets === true,
    allowPeerItemTransfer: input.allowPeerItemTransfer === true,
    notes: input.notes?.trim() || null,
    participantsCount: participantInputs.length,
    assetsCount: 0,
    confirmedAssetsCount: 0,
    returnedAssetsCount: 0,
    divergentAssetsCount: 0,
    createdAt: now,
    createdBy: user.uid,
    createdByName: userName(user),
    updatedAt: now,
    updatedBy: user.uid,
    updatedByName: userName(user)
  });

  participantInputs.forEach((participant) => {
    batch.set(doc(db!, "companies", input.companyId, "externalServices", serviceRef.id, "participants", participant.userId), {
      userId: participant.userId,
      userName: participant.userName,
      userEmail: participant.userEmail ?? null,
      roleInService: participant.roleInService ?? null,
      status: "pendente",
      addedBy: user.uid,
      addedAt: now
    });
  });

  writeEvent(batch, input.companyId, serviceRef.id, "servico_criado", user, `Servico externo criado: ${input.title.trim()}`);
  await batch.commit();
  return serviceRef.id;
}

export async function addExternalServiceParticipant(companyId: string, serviceId: string, participant: ExternalServiceParticipantInput, currentCount: number, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  batch.set(doc(db, "companies", companyId, "externalServices", serviceId, "participants", participant.userId), {
    userId: participant.userId,
    userName: participant.userName,
    userEmail: participant.userEmail ?? null,
    roleInService: participant.roleInService ?? null,
    status: "pendente",
    addedBy: user.uid,
    addedAt: now
  }, { merge: true });
  batch.update(doc(db, "companies", companyId, "externalServices", serviceId), {
    participantsCount: currentCount + 1,
    updatedAt: now,
    updatedBy: user.uid,
    updatedByName: userName(user)
  });
  writeEvent(batch, companyId, serviceId, "participante_adicionado", user, `Participante adicionado: ${participant.userName}`, { participantUserId: participant.userId });
  await batch.commit();
}

export async function addExternalServiceItem(companyId: string, service: ExternalService, asset: Asset, assignedTo: ExternalServiceParticipant | undefined, mode: ExternalServiceItem["assignmentMode"], currentCount: number, user: User, notes = ""): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  if (asset.status !== "ativo") {
    throw new Error(`O bem ${asset.patrimonyNumber} está ${asset.status === "em_manutencao" ? "em manutenção" : "indisponível"} e não pode ser vinculado a um serviço externo.`);
  }

  const serviceId = service.id;
  const plannedStartAt = service.startDate;
  const expectedReturnAt = service.expectedEndDate;

  const conflict = await checkAssetReservationConflict(companyId, asset.id, plannedStartAt, expectedReturnAt, serviceId);
  if (conflict) {
    throw new Error(buildReservationConflictMessage(conflict));
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  const itemStatus: ExternalServiceItemStatus = "pendente_confirmacao";

  batch.set(doc(db, "companies", companyId, "externalServices", serviceId, "items", asset.id), {
    assetId: asset.id,
    assetCode: asset.internalCode || asset.patrimonyNumber,
    patrimonyNumber: asset.patrimonyNumber,
    internalCode: asset.internalCode || null,
    serialNumber: asset.serialNumber ?? null,
    description: asset.description,
    mainPhotoUrl: asset.mainPhotoUrl ?? null,
    assignedToUserId: assignedTo?.userId ?? null,
    assignedToUserName: assignedTo?.userName ?? null,
    assignmentMode: mode,
    status: itemStatus,
    conditionBefore: asset.conservationState,
    exitNotes: notes.trim() || null,
    plannedStartAt,
    expectedReturnAt: expectedReturnAt ?? null,
    createdAt: now,
    updatedAt: now
  }, { merge: true });

  batch.set(doc(db, "companies", companyId, "assets", asset.id, "reservations", serviceId), {
    companyId,
    assetId: asset.id,
    serviceId,
    serviceTitle: service.title,
    serviceCode: service.serviceCode ?? null,
    itemId: asset.id,
    responsibleUserId: assignedTo?.userId ?? null,
    responsibleUserName: assignedTo?.userName ?? null,
    plannedStartAt,
    expectedReturnAt: expectedReturnAt ?? null,
    status: "reservado",
    createdAt: now,
    createdBy: user.uid
  }, { merge: true });

  batch.update(doc(db, "companies", companyId, "assets", asset.id), {
    externalServiceStatus: "reservado",
    currentExternalServiceId: serviceId,
    currentExternalServiceParticipantId: assignedTo?.userId ?? null,
    currentExternalServiceParticipantName: assignedTo?.userName ?? null,
    currentExternalServicePlannedStartAt: plannedStartAt,
    currentExternalServiceExpectedReturnAt: expectedReturnAt ?? null,
    externalServiceUpdatedAt: now,
    updatedAt: now,
    updatedBy: user.uid
  });

  const servicePayload: Record<string, number | string | FieldValue> = {
    assetsCount: currentCount + 1,
    updatedAt: now,
    updatedBy: user.uid,
    updatedByName: userName(user)
  };
  if (service.status === "aberto") {
    servicePayload.status = "aguardando_confirmacao";
  }
  batch.update(doc(db, "companies", companyId, "externalServices", serviceId), servicePayload);

  writeEvent(batch, companyId, serviceId, mode === "usuario_solicitou" ? "bem_adicionado" : "bem_atribuido", user, `Bem ${asset.patrimonyNumber} vinculado ao servico.`, { assetId: asset.id, participantUserId: assignedTo?.userId });
  await batch.commit();
  await syncAssetExternalServiceState(companyId, asset.id, user);
}

export async function updateExternalServiceItemStatus(
  companyId: string,
  serviceId: string,
  item: ExternalServiceItem,
  status: ExternalServiceItemStatus,
  user: User,
  notes = "",
  extra: { inspectionId?: string } = {}
): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  const itemPayload: Record<string, string | null | FieldValue> = {
    status,
    updatedAt: now
  };
  const assetPayload: Record<string, string | null | FieldValue> = {
    externalServiceUpdatedAt: now,
    updatedAt: now,
    updatedBy: user.uid
  };
  const reservationPayload: Record<string, string | FieldValue> = {
    updatedAt: now,
    updatedBy: user.uid
  };
  let eventType: ExternalServiceEventType = "bem_confirmado";

  if (status === "confirmado_com_responsavel") {
    itemPayload.confirmedByUserId = user.uid;
    itemPayload.confirmedByUserName = userName(user);
    itemPayload.confirmedAt = now;
    itemPayload.exitNotes = notes.trim() || item.exitNotes || null;
    if (extra.inspectionId) {
      itemPayload.pickupInspectionId = extra.inspectionId;
    }
    assetPayload.externalServiceStatus = "em_servico";
    reservationPayload.status = "em_uso";
    eventType = "bem_confirmado";
  }

  if (status === "recusado") {
    assetPayload.externalServiceStatus = "disponivel";
    assetPayload.currentExternalServiceId = null;
    assetPayload.currentExternalServiceParticipantId = null;
    assetPayload.currentExternalServiceParticipantName = null;
    reservationPayload.status = "cancelado";
    eventType = "bem_recusado";
  }

  if (status === "retorno_pendente") {
    assetPayload.externalServiceStatus = "em_retorno";
    eventType = "retorno_iniciado";
  }

  if (status === "devolvido" || status === "devolvido_com_divergencia" || status === "nao_devolvido" || status === "extraviado") {
    itemPayload.returnCheckedByUserId = user.uid;
    itemPayload.returnCheckedByUserName = userName(user);
    itemPayload.returnCheckedAt = now;
    itemPayload.actualReturnAt = now;
    if (notes.trim()) {
      itemPayload.returnNotes = notes.trim();
    }
    if (extra.inspectionId) {
      itemPayload.returnInspectionId = extra.inspectionId;
    }
    reservationPayload.actualReturnAt = now;
    assetPayload.externalServiceStatus = status === "devolvido" ? "disponivel" : status === "extraviado" ? "extraviado" : "divergente";
    if (status === "devolvido") {
      assetPayload.currentExternalServiceId = null;
      assetPayload.currentExternalServiceParticipantId = null;
      assetPayload.currentExternalServiceParticipantName = null;
    }
    reservationPayload.status = "concluido";
    eventType = status === "devolvido" ? "bem_devolvido" : "divergencia_registrada";
  }

  batch.update(doc(db, "companies", companyId, "externalServices", serviceId, "items", item.id), itemPayload);
  batch.update(doc(db, "companies", companyId, "assets", item.assetId), assetPayload);
  if (reservationPayload.status) {
    batch.set(doc(db, "companies", companyId, "assets", item.assetId, "reservations", serviceId), reservationPayload, { merge: true });
  }
  writeEvent(batch, companyId, serviceId, eventType, user, `Status do bem ${item.patrimonyNumber}: ${status}.`, { assetId: item.assetId, participantUserId: item.assignedToUserId });
  await batch.commit();
  await syncAssetExternalServiceState(companyId, item.assetId, user);
}

const resolvableDivergenceStatuses: ExternalServiceItemStatus[] = ["devolvido", "devolvido_com_divergencia", "nao_devolvido", "extraviado"];

export async function resolveExternalServiceItemDivergence(
  companyId: string,
  serviceId: string,
  item: ExternalServiceItem,
  resolution: ExternalServiceItemStatus,
  user: User,
  notes = ""
): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  if (!resolvableDivergenceStatuses.includes(resolution)) {
    throw new Error("Situação de resolução inválida.");
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  const itemPayload: Record<string, string | null | FieldValue> = {
    status: resolution,
    resolutionNotes: notes.trim() || null,
    resolvedByUserId: user.uid,
    resolvedByUserName: userName(user),
    resolvedAt: now,
    updatedAt: now
  };
  const assetPayload: Record<string, string | null | FieldValue> = {
    externalServiceUpdatedAt: now,
    updatedAt: now,
    updatedBy: user.uid,
    externalServiceStatus: resolution === "devolvido" ? "disponivel" : resolution === "extraviado" ? "extraviado" : "divergente"
  };
  if (resolution === "devolvido") {
    assetPayload.currentExternalServiceId = null;
    assetPayload.currentExternalServiceParticipantId = null;
    assetPayload.currentExternalServiceParticipantName = null;
  }

  batch.update(doc(db, "companies", companyId, "externalServices", serviceId, "items", item.id), itemPayload);
  batch.update(doc(db, "companies", companyId, "assets", item.assetId), assetPayload);
  writeEvent(
    batch,
    companyId,
    serviceId,
    "divergencia_resolvida",
    user,
    `Situação do bem ${item.patrimonyNumber} atualizada de "${formatExternalServiceItemStatusLabel(item.status)}" para "${formatExternalServiceItemStatusLabel(resolution)}".${notes.trim() ? ` Obs.: ${notes.trim()}` : ""}`,
    { assetId: item.assetId, participantUserId: item.assignedToUserId }
  );
  await batch.commit();
  await syncAssetExternalServiceState(companyId, item.assetId, user);
}

export type PickupConferenceInput = {
  /** Observacao de saida (ex.: camera sem alca). Substitui a anterior quando informada. */
  exitNotes?: string;
  approvalNotes?: string;
  pickupInspectionId?: string;
};

/**
 * Libera o bem para sair. Aceita item ainda pendente de confirmacao: na conferencia
 * presencial o gerente confere com o responsavel ao lado e libera direto, registrando
 * quem conferiu (exitChecked*) e a data real de saida na reserva.
 */
export async function releaseExternalServiceItem(companyId: string, serviceId: string, item: ExternalServiceItem, user: User, notes = "", conference: PickupConferenceInput = {}): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  if (!["pendente_confirmacao", "confirmado_com_responsavel", "em_uso"].includes(item.status)) {
    throw new Error(`O bem ${item.patrimonyNumber} não está aguardando retirada.`);
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  const itemPayload: Record<string, string | null | FieldValue> = {
    status: "em_uso",
    approvedByUserId: user.uid,
    approvedByUserName: userName(user),
    approvedAt: now,
    approvalNotes: (conference.approvalNotes ?? notes).trim() || null,
    exitCheckedByUserId: user.uid,
    exitCheckedByUserName: userName(user),
    exitCheckedAt: now,
    updatedAt: now
  };
  if (conference.exitNotes !== undefined) {
    itemPayload.exitNotes = conference.exitNotes.trim() || null;
  }
  if (conference.pickupInspectionId) {
    itemPayload.pickupInspectionId = conference.pickupInspectionId;
  }

  batch.update(doc(db, "companies", companyId, "externalServices", serviceId, "items", item.id), itemPayload);
  batch.set(doc(db, "companies", companyId, "assets", item.assetId, "reservations", serviceId), {
    status: "em_uso",
    actualStartAt: now,
    updatedAt: now,
    updatedBy: user.uid
  }, { merge: true });
  const exitNote = conference.exitNotes?.trim() ? ` Obs. de saída: ${conference.exitNotes.trim()}` : "";
  writeEvent(batch, companyId, serviceId, "saida_conferida", user, `Bem ${item.patrimonyNumber} conferido e liberado para saída.${exitNote}`, { assetId: item.assetId, participantUserId: item.assignedToUserId });
  await batch.commit();
  await syncAssetExternalServiceState(companyId, item.assetId, user);
}

/** Passa o servico para "em andamento" na primeira saida conferida. */
export async function markExternalServiceStartedIfPending(companyId: string, service: ExternalService, user: User): Promise<void> {
  if (!db || !["aberto", "aguardando_confirmacao"].includes(service.status)) {
    return;
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  batch.update(doc(db, "companies", companyId, "externalServices", service.id), {
    status: "em_andamento",
    updatedAt: now,
    updatedBy: user.uid,
    updatedByName: userName(user)
  });
  writeEvent(batch, companyId, service.id, "servico_iniciado", user, "Serviço iniciado na primeira saída conferida.");
  await batch.commit();
}

export async function reassignExternalServiceItem(companyId: string, serviceId: string, item: ExternalServiceItem, newParticipant: ExternalServiceParticipant, user: User, notes = ""): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  batch.update(doc(db, "companies", companyId, "externalServices", serviceId, "items", item.id), {
    assignedToUserId: newParticipant.userId,
    assignedToUserName: newParticipant.userName,
    assignmentMode: "gerente_atribuiu",
    status: "pendente_confirmacao",
    confirmedByUserId: null,
    confirmedByUserName: null,
    confirmedAt: null,
    approvedByUserId: null,
    approvedByUserName: null,
    approvedAt: null,
    approvalNotes: null,
    returnedByUserId: null,
    returnedByUserName: null,
    returnedAt: null,
    returnCheckedByUserId: null,
    returnCheckedByUserName: null,
    returnCheckedAt: null,
    exitNotes: notes.trim() || null,
    updatedAt: now
  });
  batch.update(doc(db, "companies", companyId, "assets", item.assetId), {
    externalServiceStatus: "reservado",
    currentExternalServiceParticipantId: newParticipant.userId,
    currentExternalServiceParticipantName: newParticipant.userName,
    externalServiceUpdatedAt: now,
    updatedAt: now,
    updatedBy: user.uid
  });
  batch.set(doc(db, "companies", companyId, "assets", item.assetId, "reservations", serviceId), {
    status: "reservado",
    responsibleUserId: newParticipant.userId,
    responsibleUserName: newParticipant.userName,
    updatedAt: now,
    updatedBy: user.uid
  }, { merge: true });
  writeEvent(batch, companyId, serviceId, "bem_reatribuido", user, `Bem ${item.patrimonyNumber} reatribuido para ${newParticipant.userName}.`, { assetId: item.assetId, participantUserId: newParticipant.userId });
  await batch.commit();
  await syncAssetExternalServiceState(companyId, item.assetId, user);
}

export async function markExternalServiceItemReturned(
  companyId: string,
  serviceId: string,
  item: ExternalServiceItem,
  user: User,
  extra: { returnInspectionId?: string; returnNotes?: string } = {}
): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  const itemPayload: Record<string, string | FieldValue> = {
    status: "retorno_confirmado",
    returnedByUserId: user.uid,
    returnedByUserName: userName(user),
    returnedAt: now,
    updatedAt: now
  };
  if (extra.returnInspectionId) {
    itemPayload.returnInspectionId = extra.returnInspectionId;
  }
  if (extra.returnNotes?.trim()) {
    itemPayload.returnNotes = extra.returnNotes.trim();
  }
  batch.update(doc(db, "companies", companyId, "externalServices", serviceId, "items", item.id), itemPayload);
  batch.update(doc(db, "companies", companyId, "assets", item.assetId), {
    externalServiceStatus: "em_retorno",
    externalServiceUpdatedAt: now,
    updatedAt: now,
    updatedBy: user.uid
  });
  writeEvent(batch, companyId, serviceId, "retorno_confirmado_usuario", user, `Retorno do bem ${item.patrimonyNumber} confirmado pelo usuario${extra.returnInspectionId ? " com POP de retorno preenchido" : ""}, aguardando conferencia.`, { assetId: item.assetId, participantUserId: item.assignedToUserId });
  await batch.commit();
}

export async function startExternalServiceReturn(companyId: string, service: ExternalService, items: ExternalServiceItem[], participants: ExternalServiceParticipant[], user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const serviceId = service.id;
  const batch = writeBatch(db);
  const now = serverTimestamp();
  const itemsToReturn = items.filter((item) => item.status === "em_uso" || item.status === "confirmado_com_responsavel");

  itemsToReturn.forEach((item) => {
    batch.update(doc(db!, "companies", companyId, "externalServices", serviceId, "items", item.id), {
      status: "retorno_pendente",
      updatedAt: now
    });
    batch.update(doc(db!, "companies", companyId, "assets", item.assetId), {
      externalServiceStatus: "em_retorno",
      externalServiceUpdatedAt: now,
      updatedAt: now,
      updatedBy: user.uid
    });
  });

  batch.update(doc(db, "companies", companyId, "externalServices", serviceId), {
    status: "em_retorno",
    updatedAt: now,
    updatedBy: user.uid,
    updatedByName: userName(user)
  });
  writeEvent(batch, companyId, serviceId, "retorno_iniciado", user, `Retorno do servico iniciado. ${itemsToReturn.length} bem(ns) sinalizado(s) para devolucao.`);

  const holderUserIds = new Set(itemsToReturn.map((item) => item.assignedToUserId).filter(Boolean) as string[]);
  participants.forEach((participant) => {
    const isHolder = holderUserIds.has(participant.userId);
    const notificationRef = doc(collection(db!, "companies", companyId, "notifications"));
    batch.set(notificationRef, {
      companyId,
      recipientUserId: participant.userId,
      type: isHolder ? "return_started_holder" : "return_started_no_items",
      title: "Retorno de serviço externo iniciado",
      body: isHolder
        ? `O retorno do serviço "${service.title}" foi iniciado. Devolva o(s) bem(ns) que estão com você.`
        : `O retorno do serviço "${service.title}" foi iniciado. Você não está com nenhum equipamento deste serviço.`,
      read: false,
      externalServiceId: serviceId,
      createdAt: now,
      createdBy: user.uid
    });
  });

  await batch.commit();
}

export async function updateExternalServiceItemNotes(companyId: string, serviceId: string, item: ExternalServiceItem, notes: { exitNotes?: string; returnNotes?: string; divergenceNotes?: string }, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const batch = writeBatch(db);
  const payload: Record<string, string | null | FieldValue> = {
    updatedAt: serverTimestamp()
  };

  if (notes.exitNotes !== undefined) {
    payload.exitNotes = notes.exitNotes.trim() || null;
  }
  if (notes.returnNotes !== undefined) {
    payload.returnNotes = notes.returnNotes.trim() || null;
  }
  if (notes.divergenceNotes !== undefined) {
    payload.divergenceNotes = notes.divergenceNotes.trim() || null;
  }

  batch.update(doc(db, "companies", companyId, "externalServices", serviceId, "items", item.id), payload);
  writeEvent(batch, companyId, serviceId, "divergencia_registrada", user, `Observacoes do bem ${item.patrimonyNumber} atualizadas.`, { assetId: item.assetId, participantUserId: item.assignedToUserId });
  await batch.commit();
}

export async function updateExternalServiceStatus(companyId: string, serviceId: string, status: ExternalServiceStatus, user: User, items: ExternalServiceItem[] = []): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  const payload: Record<string, string | FieldValue | null> = {
    status,
    updatedAt: now,
    updatedBy: user.uid,
    updatedByName: userName(user)
  };

  if (status === "concluido") {
    payload.actualEndDate = now;
  }

  batch.update(doc(db, "companies", companyId, "externalServices", serviceId), payload);

  if (status === "cancelado") {
    const openStatuses: ExternalServiceItemStatus[] = ["pendente_confirmacao", "confirmado_com_responsavel", "em_uso", "retorno_pendente", "retorno_confirmado"];
    items.filter((item) => openStatuses.includes(item.status)).forEach((item) => {
      batch.set(doc(db!, "companies", companyId, "assets", item.assetId, "reservations", serviceId), {
        status: "cancelado",
        updatedAt: now,
        updatedBy: user.uid
      }, { merge: true });
    });
  }

  const eventByStatus: Partial<Record<ExternalServiceStatus, ExternalServiceEventType>> = {
    em_andamento: "servico_iniciado",
    em_retorno: "retorno_iniciado",
    concluido: "servico_concluido",
    cancelado: "servico_cancelado"
  };
  const eventType = eventByStatus[status];
  if (eventType) {
    writeEvent(batch, companyId, serviceId, eventType, user, `Status do servico externo alterado para ${status}.`);
  }

  await batch.commit();

  if (status === "cancelado") {
    const affectedAssetIds = Array.from(new Set(items.map((item) => item.assetId)));
    await Promise.all(affectedAssetIds.map((assetId) => syncAssetExternalServiceState(companyId, assetId, user)));
  }
}

const ITEM_STATUSES_HOLDING_RESERVATION: ExternalServiceItemStatus[] = ["pendente_confirmacao", "confirmado_com_responsavel", "em_uso", "retorno_pendente", "retorno_confirmado"];

export async function updateExternalServiceDetails(companyId: string, service: ExternalService, input: CreateExternalServiceInput, user: User, items: ExternalServiceItem[] = []): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  if (!input.title.trim()) {
    throw new Error("Informe o nome do servico externo.");
  }

  if (input.expectedEndDate && input.expectedEndDate < input.startDate) {
    throw new Error("A previsão de fim não pode ser anterior ao início.");
  }

  const datesChanged = input.startDate !== service.startDate || (input.expectedEndDate || undefined) !== (service.expectedEndDate || undefined);
  const itemsHoldingReservation = items.filter((item) => ITEM_STATUSES_HOLDING_RESERVATION.includes(item.status));

  // Mudou o periodo: as reservas dos bens precisam acompanhar, senao o calendario e a
  // checagem de conflito passam a usar datas antigas. Confere antes de gravar.
  if (datesChanged) {
    for (const item of itemsHoldingReservation) {
      const conflict = await checkAssetReservationConflict(companyId, item.assetId, input.startDate, input.expectedEndDate || undefined, service.id);
      if (conflict) {
        throw new Error(`Não dá para mudar o período: o bem ${item.patrimonyNumber} já está reservado no serviço "${conflict.serviceTitle}" nas novas datas.`);
      }
    }
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  if (datesChanged) {
    itemsHoldingReservation.forEach((item) => {
      batch.update(doc(db!, "companies", companyId, "externalServices", service.id, "items", item.id), {
        plannedStartAt: input.startDate,
        expectedReturnAt: input.expectedEndDate || null,
        updatedAt: now
      });
      batch.set(doc(db!, "companies", companyId, "assets", item.assetId, "reservations", service.id), {
        plannedStartAt: input.startDate,
        expectedReturnAt: input.expectedEndDate || null,
        updatedAt: now,
        updatedBy: user.uid
      }, { merge: true });
    });
  }
  batch.update(doc(db, "companies", companyId, "externalServices", service.id), {
    title: input.title.trim(),
    description: input.description.trim(),
    startDate: input.startDate,
    expectedEndDate: input.expectedEndDate || null,
    location: input.location?.trim() || null,
    clientName: input.clientName?.trim() || null,
    jobType: input.jobType?.trim() || null,
    notes: input.notes?.trim() || null,
    allowSelfEnrollment: input.allowSelfEnrollment === true,
    allowSelfAssignAssets: input.allowSelfAssignAssets === true,
    allowPeerItemTransfer: input.allowPeerItemTransfer === true,
    updatedAt: now,
    updatedBy: user.uid,
    updatedByName: userName(user)
  });
  writeEvent(batch, companyId, service.id, "servico_atualizado", user, `Dados do servico externo atualizados: ${input.title.trim()}${datesChanged ? ` (período: ${formatPdfDate(input.startDate)} a ${formatPdfDate(input.expectedEndDate)})` : ""}`);
  await batch.commit();

  if (datesChanged) {
    // O "servico atual" do bem depende de todas as reservas dele, nao so desta
    await Promise.all(Array.from(new Set(itemsHoldingReservation.map((item) => item.assetId))).map((assetId) => syncAssetExternalServiceState(companyId, assetId, user)));
  }
}

export async function archiveExternalService(companyId: string, service: ExternalService, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  if (service.status !== "concluido" && service.status !== "cancelado") {
    throw new Error("Apenas serviços concluídos ou cancelados podem ser arquivados.");
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  batch.update(doc(db, "companies", companyId, "externalServices", service.id), {
    deletedAt: now,
    deletedBy: user.uid,
    updatedAt: now,
    updatedBy: user.uid,
    updatedByName: userName(user)
  });
  writeEvent(batch, companyId, service.id, "servico_atualizado", user, "Serviço externo arquivado.");
  await batch.commit();
}

export async function restoreExternalService(companyId: string, serviceId: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const batch = writeBatch(db);
  const now = serverTimestamp();
  batch.update(doc(db, "companies", companyId, "externalServices", serviceId), {
    deletedAt: null,
    deletedBy: null,
    updatedAt: now,
    updatedBy: user.uid,
    updatedByName: userName(user)
  });
  writeEvent(batch, companyId, serviceId, "servico_atualizado", user, "Serviço externo restaurado do arquivo.");
  await batch.commit();
}

export async function saveExternalServicePdfEvent(companyId: string, serviceId: string, user: User): Promise<void> {
  if (!db) {
    return;
  }

  const batch = writeBatch(db);
  writeEvent(batch, companyId, serviceId, "pdf_gerado", user, "PDF do servico externo gerado.");
  await batch.commit();
}

export type ExternalServicePdfOptions = {
  company: Company;
  service: ExternalService;
  participants: ExternalServiceParticipant[];
  items: ExternalServiceItem[];
  generatedBy: string;
  imobiLogoUrl?: string;
  version?: string;
  /** Mantido por compatibilidade: um unico participante. */
  participantId?: string;
  /** Filtra o extrato pelos responsaveis escolhidos. Vazio = todos. */
  participantIds?: string[];
  includeSignatures?: boolean;
  /** Registra no historico do servico que o PDF foi emitido. */
  currentUser?: User | null;
};

type PdfParticipantGroup = {
  key: string;
  name: string;
  roleInService?: string;
  items: ExternalServiceItem[];
};

type PdfWithAutoTable = jsPDF & { lastAutoTable?: { finalY: number } };

const PDF_PAGE_WIDTH = 297;
const PDF_PAGE_HEIGHT = 210;
const PDF_MARGIN = 14;
const PDF_BRAND: [number, number, number] = [15, 118, 110];

export async function exportExternalServicePdf({
  company,
  service,
  participants,
  items,
  generatedBy,
  imobiLogoUrl,
  version = "0.1.70",
  participantId,
  participantIds,
  includeSignatures = true,
  currentUser
}: ExternalServicePdfOptions): Promise<void> {
  const selectedIds = participantIds?.length ? participantIds : participantId ? [participantId] : [];
  const isFiltered = selectedIds.length > 0;
  const filteredItems = isFiltered ? items.filter((item) => item.assignedToUserId && selectedIds.includes(item.assignedToUserId)) : items;
  const selectedParticipants = isFiltered ? participants.filter((participant) => selectedIds.includes(participant.userId)) : participants;
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" }) as PdfWithAutoTable;
  const lastTableY = (fallback: number) => pdf.lastAutoTable?.finalY ?? fallback;
  const generatedAt = new Date().toLocaleString("pt-BR");
  const serviceCode = service.serviceCode ?? externalServiceCode(service.id);
  const title = selectedIds.length === 1 ? "Termo de Responsabilidade Temporária" : "Extrato do Serviço Externo";
  const [imobiLogo, companyLogo] = await Promise.all([loadPdfImage(imobiLogoUrl, true), loadPdfImage(company.logoUrl, true)]);
  const qrDataUrl = await QRCode.toDataURL(externalServiceQrPayload(company.id, service.id, service.serviceCode), { margin: 0, width: 160 });
  const serviceQr = await normalizeImageDataUrl(qrDataUrl, true);
  const itemPhotos = new Map<string, PdfImage | null>();
  await Promise.all(filteredItems.map(async (item) => {
    itemPhotos.set(item.id, await loadPdfImage(item.mainPhotoUrl, false));
  }));

  // Cabecalho: titulo, empresa, logos e QR do servico
  pdf.setFillColor(...PDF_BRAND);
  pdf.rect(0, 0, PDF_PAGE_WIDTH, 3, "F");
  pdf.setTextColor(15, 23, 42);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.text(title, PDF_MARGIN, 15);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(71, 85, 105);
  pdf.text(`${company.tradeName}${company.cnpj ? ` | CNPJ: ${company.cnpj}` : ""}`, PDF_MARGIN, 21);

  let logoX = PDF_PAGE_WIDTH - PDF_MARGIN - 22;
  if (serviceQr) {
    pdf.addImage(serviceQr.dataUrl, serviceQr.format, logoX, 7, 22, 22, undefined, "FAST");
    logoX -= 26;
  }
  if (companyLogo) {
    pdf.addImage(companyLogo.dataUrl, companyLogo.format, logoX, 9, 22, 16, undefined, "FAST");
    logoX -= 28;
  }
  if (imobiLogo) {
    pdf.addImage(imobiLogo.dataUrl, imobiLogo.format, logoX, 9, 24, 16, undefined, "FAST");
  }

  // Bloco de dados do servico
  const infoTop = 31;
  pdf.setDrawColor(226, 232, 240);
  pdf.setFillColor(248, 250, 252);
  pdf.roundedRect(PDF_MARGIN, infoTop, PDF_PAGE_WIDTH - PDF_MARGIN * 2, 22, 2, 2, "FD");
  const infoColumns: Array<[string, string]> = [
    ["Serviço", service.title],
    ["Código", serviceCode],
    ["Período", `${formatPdfDate(service.startDate)} a ${formatPdfDate(service.expectedEndDate)}`],
    ["Situação", formatExternalServiceStatusLabel(service.status)],
    ["Cliente", service.clientName || "-"],
    ["Local", service.location || "-"]
  ];
  const columnWidth = (PDF_PAGE_WIDTH - PDF_MARGIN * 2 - 8) / 3;
  infoColumns.forEach(([label, value], index) => {
    const x = PDF_MARGIN + 4 + (index % 3) * columnWidth;
    const y = infoTop + 6 + Math.floor(index / 3) * 9;
    pdf.setFontSize(7);
    pdf.setTextColor(100, 116, 139);
    pdf.text(label.toUpperCase(), x, y);
    pdf.setFontSize(9);
    pdf.setTextColor(15, 23, 42);
    pdf.text(pdf.splitTextToSize(value, columnWidth - 4)[0] ?? "-", x, y + 4);
  });

  // Resumo por participante
  const groups: PdfParticipantGroup[] = selectedParticipants.map((participant) => ({
    key: participant.userId,
    name: participant.userName,
    roleInService: participant.roleInService,
    items: filteredItems.filter((item) => item.assignedToUserId === participant.userId)
  }));
  const withoutResponsible = filteredItems.filter((item) => !item.assignedToUserId || !participants.some((participant) => participant.userId === item.assignedToUserId));
  if (!isFiltered && withoutResponsible.length) {
    groups.push({ key: "sem-responsavel", name: "Sem responsável definido", items: withoutResponsible });
  }

  autoTable(pdf, {
    startY: infoTop + 27,
    margin: { left: PDF_MARGIN, right: PDF_MARGIN, bottom: 16 },
    head: [["Participante", "Função", "Bens", "Em uso", "Devolvidos", "Pendências"]],
    body: groups.map((group) => [
      group.name,
      group.roleInService || "-",
      String(group.items.length),
      String(group.items.filter((item) => ["em_uso", "confirmado_com_responsavel", "retorno_pendente", "retorno_confirmado"].includes(item.status)).length),
      String(group.items.filter((item) => item.status === "devolvido").length),
      String(group.items.filter((item) => ["devolvido_com_divergencia", "nao_devolvido", "extraviado"].includes(item.status)).length)
    ]),
    styles: { fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: PDF_BRAND },
    columnStyles: { 2: { halign: "center" }, 3: { halign: "center" }, 4: { halign: "center" }, 5: { halign: "center" } }
  });

  // Uma secao por responsavel, com os bens e o campo de assinatura
  let cursorY = lastTableY(60);
  groups.filter((group) => group.items.length).forEach((group) => {
    let startY = cursorY + 9;
    if (startY > PDF_PAGE_HEIGHT - 50) {
      pdf.addPage();
      startY = 18;
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(15, 23, 42);
    pdf.text(`${group.name} - ${group.items.length} bem(ns)`, PDF_MARGIN, startY);
    pdf.setFont("helvetica", "normal");

    const statusByRow = group.items.map((item) => item.status);
    autoTable(pdf, {
      startY: startY + 3,
      margin: { left: PDF_MARGIN, right: PDF_MARGIN, bottom: 16 },
      head: [["Foto", "Patrimônio", "Descrição", "Série", "Situação", "Saída", "Retorno", "Observações"]],
      body: group.items.map((item) => [
        "",
        item.patrimonyNumber,
        item.description,
        item.serialNumber ?? "-",
        formatExternalServiceItemStatusLabel(item.status),
        formatPdfAudit(item.exitCheckedByUserName ?? item.approvedByUserName ?? item.confirmedByUserName, item.exitCheckedAt ?? item.approvedAt ?? item.confirmedAt),
        formatPdfAudit(item.returnCheckedByUserName ?? item.returnedByUserName, item.returnCheckedAt ?? item.returnedAt),
        formatExternalServiceNotes(item)
      ]),
      styles: { fontSize: 7, cellPadding: 1.5, minCellHeight: 14, valign: "middle" },
      columnStyles: {
        0: { cellWidth: 14 },
        1: { cellWidth: 24 },
        2: { cellWidth: 52 },
        3: { cellWidth: 24 },
        4: { cellWidth: 30 },
        5: { cellWidth: 34 },
        6: { cellWidth: 34 },
        7: { cellWidth: "auto" }
      },
      headStyles: { fillColor: [51, 65, 85] },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 4) {
          data.cell.styles.cellPadding = { top: 1.5, right: 1.5, bottom: 1.5, left: 6 };
        }
      },
      didDrawCell: (data) => {
        if (data.section !== "body") {
          return;
        }

        const item = group.items[data.row.index];
        if (!item) {
          return;
        }

        if (data.column.index === 0) {
          const image = itemPhotos.get(item.id);
          pdf.setDrawColor(203, 213, 225);
          pdf.roundedRect(data.cell.x + 2, data.cell.y + 2, 10, 10, 1, 1);
          if (image) {
            pdf.addImage(image.dataUrl, image.format, data.cell.x + 2.5, data.cell.y + 2.5, 9, 9, undefined, "FAST");
          }
        }

        if (data.column.index === 4) {
          const color = externalServiceStatusColor(statusByRow[data.row.index]);
          pdf.setFillColor(color[0], color[1], color[2]);
          pdf.circle(data.cell.x + 3, data.cell.y + data.cell.height / 2, 1.05, "F");
        }
      }
    });
    cursorY = lastTableY(startY);

    if (!includeSignatures || group.key === "sem-responsavel") {
      return;
    }

    let signatureY = cursorY + 6;
    if (signatureY > PDF_PAGE_HEIGHT - 34) {
      pdf.addPage();
      signatureY = 18;
    }

    pdf.setFontSize(8);
    pdf.setTextColor(71, 85, 105);
    const declaration = `Declaro ter conferido e recebido os ${group.items.length} bem(ns) acima em caráter temporário para o serviço "${service.title}", comprometendo-me a zelar por eles e devolvê-los nas condições registradas até ${formatPdfDate(service.expectedEndDate)}.`;
    pdf.text(pdf.splitTextToSize(declaration, PDF_PAGE_WIDTH - PDF_MARGIN * 2), PDF_MARGIN, signatureY);
    const lineY = signatureY + 18;
    const lineWidth = 80;
    pdf.setDrawColor(148, 163, 184);
    [
      { x: PDF_MARGIN, label: `${group.name} (responsável)` },
      { x: PDF_MARGIN + lineWidth + 14, label: "Conferente da saída" },
      { x: PDF_MARGIN + (lineWidth + 14) * 2, label: "Conferente do retorno" }
    ].forEach((field) => {
      pdf.line(field.x, lineY, field.x + lineWidth, lineY);
      pdf.setFontSize(7);
      pdf.text(field.label, field.x, lineY + 4);
      pdf.text("Data: ____/____/______", field.x + lineWidth - 30, lineY + 4);
    });
    cursorY = lineY + 4;
  });

  if (!groups.some((group) => group.items.length)) {
    pdf.setFontSize(10);
    pdf.setTextColor(100, 116, 139);
    pdf.text("Nenhum bem vinculado aos participantes selecionados.", PDF_MARGIN, cursorY + 10);
  }

  const pageCount = pdf.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    pdf.setPage(page);
    pdf.setFontSize(7);
    pdf.setTextColor(100, 116, 139);
    pdf.text(`IMOBI v${version} | ${serviceCode} | Gerado em ${generatedAt} por ${generatedBy}`, PDF_MARGIN, PDF_PAGE_HEIGHT - 8);
    pdf.text(`Página ${page} de ${pageCount}`, PDF_PAGE_WIDTH - PDF_MARGIN, PDF_PAGE_HEIGHT - 8, { align: "right" });
  }

  const suffix = selectedIds.length === 1 ? (selectedParticipants[0]?.userName ?? "participante") : isFiltered ? "participantes" : "extrato";
  pdf.save(`${`${service.title}-${suffix}`.replace(/[^a-z0-9]+/gi, "-").toLowerCase().replace(/^-|-$/g, "")}.pdf`);

  if (currentUser) {
    try {
      await saveExternalServicePdfEvent(company.id, service.id, currentUser);
    } catch {
      // O PDF ja foi salvo; falhar o registro no historico nao deve virar erro para o usuario
    }
  }
}

function formatPdfAudit(name?: string, value?: string) {
  if (!value) {
    return "-";
  }

  return `${name ?? "-"}\n${formatPdfDateTime(value)}`;
}

function formatPdfDateTime(value?: string) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(date);
}

function formatPdfDate(value?: string) {
  if (!value) {
    return "-";
  }

  // Datas sem horario ("AAAA-MM-DD") sao locais: new Date() as leria em UTC e voltaria um dia
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(new Date(`${value}T00:00:00`));
  }

  return formatPdfDateTime(value);
}

function formatExternalServiceStatusLabel(status: ExternalServiceStatus) {
  const labels: Record<ExternalServiceStatus, string> = {
    rascunho: "Rascunho",
    aberto: "Aberto",
    aguardando_confirmacao: "Aguardando confirmação",
    em_andamento: "Em andamento",
    em_retorno: "Em retorno",
    concluido: "Concluído",
    cancelado: "Cancelado"
  };
  return labels[status] ?? status;
}

function formatExternalServiceItemStatusLabel(status: ExternalServiceItemStatus) {
  const labels: Record<ExternalServiceItemStatus, string> = {
    pendente_confirmacao: "Pendente de confirmação",
    confirmado_com_responsavel: "Aguardando liberação",
    recusado: "Recusado",
    em_uso: "Em uso",
    retorno_pendente: "Retorno pendente",
    retorno_confirmado: "Aguardando aprovação do retorno",
    devolvido: "Devolvido",
    devolvido_com_divergencia: "Devolvido com divergência",
    nao_devolvido: "Não devolvido",
    extraviado: "Extraviado"
  };
  return labels[status] ?? status;
}

function externalServiceStatusColor(status: ExternalServiceItemStatus): [number, number, number] {
  if (status === "devolvido") return [22, 163, 74];
  if (status === "pendente_confirmacao" || status === "retorno_pendente" || status === "retorno_confirmado") return [217, 119, 6];
  if (status === "devolvido_com_divergencia" || status === "nao_devolvido" || status === "extraviado") return [220, 38, 38];
  if (status === "em_uso" || status === "confirmado_com_responsavel") return [37, 99, 235];
  return [100, 116, 139];
}

function formatExternalServiceNotes(item: ExternalServiceItem) {
  return [
    item.exitNotes ? `Retirada: ${item.exitNotes}` : "",
    item.approvalNotes ? `Aprovação: ${item.approvalNotes}` : "",
    item.returnNotes ? `Retorno: ${item.returnNotes}` : "",
    item.divergenceNotes ? `Divergência: ${item.divergenceNotes}` : ""
  ].filter(Boolean).join(" | ") || "-";
}
